import React, { Component } from 'react';
import Loadable from 'react-loadable';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import MetaTags from 'react-meta-tags';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import axios from 'axios';
import { get } from 'lodash';
import 'antd/dist/reset.css';
import Login from './components/auth/Login/Login';
import Logout from '~/components/auth/Logout';
import PrivateRoute from './components/PrivateRoute';
import history from './services/history';
import * as userApi from '~/api/userApi';
import { logout } from '~/redux/actions/authActions';
import { HTTP_STATUS_CODE, ENV_URLS } from '~/common/constants';

import Loader from '@shared/Loader';
import { message } from 'antd';
import { rootRoutes } from './common/routerConfig';

const text = {
  sessionExpired: 'Your session has expired. Please login again.',
  unauthorizedError:
    'Sorry, you trying accomplish action wich requires authorization.'
};

const AuthorizedContent = Loadable({
  loader: () => import('~/components/AuthorizedContent'),
  loading: Loader
});

const FirstTimeLoginInt = Loadable({
  loader: () => import('~/components/auth/FirstTimeLogin/Internal'),
  loading: Loader
});

const FirstTimeLoginExt = Loadable({
  loader: () => import('~/components/auth/FirstTimeLogin/External'),
  loading: Loader
});

const RequestResetPassword = Loadable({
  loader: () => import('~/components/auth/RequestResetPassword'),
  loading: Loader
});

const ChangeExpiredPassword = Loadable({
  loader: () => import('~/components/auth/ChangeExpiredPassword'),
  loading: Loader
});

const ResetPassword = Loadable({
  loader: () => import('~/components/auth/ResetPassword'),
  loading: Loader
});

const ExtAuthenticateSuccess = Loadable({
  loader: () =>
    import(
      '~/components/auth/ExtAuthenticateSuccess/microsoftExtAuthenicateSuccess'
    ),
  loading: Loader
});

const GoogleExtAuthenticateSuccess = Loadable({
  loader: () =>
    import(
      '~/components/auth/ExtAuthenticateSuccess/googleExtAuthenicateSuccess'
    ),
  loading: Loader
});

const AccountDisabled = Loadable({
  loader: () => import('~/components/auth/AccountDisabled'),
  loading: Loader
});

const PrivacyPolicy = Loadable({
  loader: () => import('~/components/shared/PrivacyPolicy'),
  loading: Loader
});

const setEnvironmentUrl = env => {
  if (env === ENV_URLS.STAGE1.url) {
    return ENV_URLS.STAGE1.tag;
  } else if (env === ENV_URLS.STAGE2.url) {
    return ENV_URLS.STAGE2.tag;
  } else if (env === ENV_URLS.STAGE3.url) {
    return ENV_URLS.STAGE3.tag;
  } else if (env === ENV_URLS.STAGE4.url) {
    return ENV_URLS.STAGE4.tag;
  } else if (env === ENV_URLS.STAGE5.url) {
    return ENV_URLS.STAGE5.tag;
  } else if (env === ENV_URLS.STAGE6.url) {
    return ENV_URLS.STAGE6.tag;
  } else if (env === ENV_URLS.PROD.url) {
    return ENV_URLS.PROD.tag;
  } else if (env === ENV_URLS.S9_STAGE1.url) {
    return ENV_URLS.S9_STAGE1.tag;
  } else if (env === ENV_URLS.W5_STAGE1.url) {
    return ENV_URLS.W5_STAGE1.tag;
  } else if (env === ENV_URLS.S9_PROD.url) {
    return ENV_URLS.S9_PROD.tag;
  } else if (env === ENV_URLS.W5_PROD.url) {
    return ENV_URLS.W5_PROD.tag;
  }
};

class App extends Component {
  state = {
    isAuthorizationChecked: false
  };
  componentDidMount() {
    const GTM_ID = setEnvironmentUrl(window.location.origin);
    const head = document.getElementsByTagName('head')[0];
    const bodyTag = document.getElementsByTagName('body')[0];
    const script = document.createElement('script');
    const noscript = document.createElement('noscript');
    script.innerText = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${GTM_ID}')`;
    noscript.innerText = `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}"height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    head.insertBefore(script, head.firstChild);
    bodyTag.insertBefore(noscript, bodyTag.firstChild);

    message.config({
      maxCount: 1
    });

    axios.interceptors.response.use(null, async error => {
      const { logout, isLoggedIn, login } = this.props;
      const status = get(error, 'response.status');

      if (
        !this.state.isAuthorizationChecked &&
        status === HTTP_STATUS_CODE.unauthorized &&
        isLoggedIn
      ) {
        try {
          // check if user have access to own profile
          // otherwise assume user isn't logged in.
          this.setState({ isAuthorizationChecked: true });
          await userApi.getUser(login);
          message.warning(text.unauthorizedError);
        } catch (userProfileError) {
          this.setState({ isAuthorizationChecked: false });
          const statusProfileError = get(userProfileError, 'response.status');
          if (statusProfileError === HTTP_STATUS_CODE.unauthorized) {
            // timeout is necessary
            // should wait until component finish update state
            setTimeout(() => logout(text.sessionExpired));
          }
        }
      }

      return Promise.reject(error);
    });
  }
  render() {
    return (
      <>
        <MetaTags>
          <title>AccelAero</title>
        </MetaTags>

        <Router history={history}>
          <Switch>
            <Route exact path={rootRoutes.logout} component={Logout} />
            <Route exact path={rootRoutes.login} component={Login} />
            {/*  */}
            {/* First time login for INT & EXT users w/ EMAIL */}
            {/* User need to be status INVITED and Auth method Email  */}
            {/* User will recieve email with link /:hash */}
            <Route
              path={rootRoutes.firstTimeLogin}
              component={FirstTimeLoginInt}
            />
            {/*  */}
            {/* First time login for EXT users w/ username */}
            {/* User should be with status NEW and Auth method Username */}
            <Route
              path={rootRoutes.changePassword}
              component={FirstTimeLoginExt}
            />
            <Route
              path={rootRoutes.requestResetPassword}
              component={RequestResetPassword}
            />
            <Route
              path={rootRoutes.changeExpiredPassword}
              component={ChangeExpiredPassword}
            />
            <Route path={rootRoutes.resetPassword} component={ResetPassword} />
            <Route
              path={rootRoutes.extAuthenticateSuccess}
              component={ExtAuthenticateSuccess}
            />
            <Route
              path={rootRoutes.googleExtAuthenticateSuccess}
              component={GoogleExtAuthenticateSuccess}
            />
            <Route
              exact
              path="/"
              render={() => <Redirect to={rootRoutes.login} />}
            />
            <Route
              exact
              path={rootRoutes.disabledAccount}
              component={AccountDisabled}
            />
            <Route path={rootRoutes.privacyPolicy} component={PrivacyPolicy} />
            <PrivateRoute component={AuthorizedContent} />
          </Switch>
        </Router>
      </>
    );
  }
}

App.propTypes = {
  logout: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool,
  login: PropTypes.string
};

const mapStateToProps = state => ({
  isLoggedIn: state.auth.isLoggedIn,
  login: state.auth.login
});

const mapDispatchToProps = {
  logout
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(App);
